import React, { FC, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../models";
import { getSalesAgent, getSelectedAgent } from "../../../state/selectors/selectors";

const HIDE_SALESFORCE_CHAT_LAUNCHER_STYLE_ID = "support-chat-hide-salesforce-launcher";

declare global {
    interface Window {
        embeddedservice_bootstrap?: {
            settings: { language: string };
            init: (orgId: string, name: string, url: string, options: { scrt2URL: string }) => void;
            prechatAPI?: {
                setHiddenPrechatFields: (fields: Record<string, string>) => void;
            };
            utilAPI?: {
                launchChat: () => void;
                removeAllComponents: () => void;
                sendTextMessage: (text: string) => Promise<void>;
            };
            userVerificationAPI?: {
                clearSession: (clearStorage: boolean) => Promise<void>;
            };
        };
    }
}

const clickSalesforceChatButton = (): boolean => {
    const sfButton = document.querySelector<HTMLElement>(
        "embeddedservice-chat-button button, .embeddedServiceSidebarMinimizedDefaultUI button, [class*='minimizedButton'] button, [class*='chatButton'] button",
    );
    if (sfButton) {
        sfButton.click();
        return true;
    }
    return false;
};

interface Props {
    initialMessage?: string;
    chatMessage?: string;
    manualAgentName?: string;
    manualDealerCode?: string;
    phoneNumber?: string;
    onOpenChange: (open: boolean) => void;
    onSessionEnded?: () => void;
    open: boolean;
}

const SupportChatPanel: FC<Props> = ({
    open,
    onOpenChange,
    onSessionEnded,
    initialMessage,
    chatMessage,
    manualAgentName,
    manualDealerCode,
    phoneNumber,
}) => {
    const sfReadyRef = useRef(false);
    const pendingClickRef = useRef(false);
    const prechatFieldsRef = useRef<Record<string, string>>({});
    const resetAndReinitRef = useRef<(() => void) | null>(null);
    const chatMessageRef = useRef(chatMessage);
    const initialMessageSentRef = useRef(false);
    chatMessageRef.current = chatMessage;
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const selectedAgent = useSelector((state: RootState) => getSelectedAgent(state));
    const sfChat = useSelector((state: RootState) => state.config?.salesforceChat);
    const bootstrapUrl = sfChat?.bootstrapUrl;
    const sfOrgId = sfChat?.orgId;
    const sfInitUrl = sfChat?.initUrl;
    const sfScrt2Url = sfChat?.scrt2Url;

    const effectiveAgent = selectedAgent?.firstName || selectedAgent?.lastName ? selectedAgent : salesAgent;
    const reduxAgentName = effectiveAgent
        ? [effectiveAgent.firstName, effectiveAgent.lastName].filter(Boolean).join(" ")
        : "";
    const agentName = manualAgentName || reduxAgentName;
    const dealerCode = manualDealerCode || salesAgent?.dealerCode || "";

    // Keep prechat fields up to date whenever values change
    useEffect(() => {
        prechatFieldsRef.current = {
            UserName: agentName,
            Skill: "Sales Support",
            ...(phoneNumber ? { PhoneNumber: phoneNumber } : {}),
            ...(initialMessage ? { ShortDescription: initialMessage } : {}),
            ...(dealerCode ? { RetailerCode: dealerCode } : {}),
        };
        if (window.embeddedservice_bootstrap?.prechatAPI) {
            window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(prechatFieldsRef.current);
        }
    }, [agentName, phoneNumber, initialMessage, manualAgentName, manualDealerCode, dealerCode]);

    useEffect(() => {
        let styleElement = document.getElementById(HIDE_SALESFORCE_CHAT_LAUNCHER_STYLE_ID) as HTMLStyleElement | null;

        if (!styleElement) {
            styleElement = document.createElement("style");
            styleElement.id = HIDE_SALESFORCE_CHAT_LAUNCHER_STYLE_ID;
            styleElement.textContent = `
                embeddedservice-chat-button,
                .embeddedServiceHelpButton,
                .embeddedServiceSidebarMinimizedDefaultUI,
                [class*="embeddedMessagingConversationButton"] {
                    display: none !important;
                }
            `;
            document.head.appendChild(styleElement);
        }
    }, []);

    useEffect(() => {
        if (!bootstrapUrl || !sfOrgId || !sfInitUrl || !sfScrt2Url) {
            return;
        }

        const loadAndInit = () => {
            const script = document.createElement("script");
            script.id = "embedded-messaging-bootstrap";
            script.type = "text/javascript";
            script.src = `${bootstrapUrl}/assets/js/bootstrap.min.js`;
            script.addEventListener("load", initEmbeddedMessaging, { once: true });
            script.addEventListener("error", () => {
                // oxlint-disable-next-line no-console
                console.error("[SupportChatPanel] Failed to load bootstrap script from", bootstrapUrl);
            });
            document.head.appendChild(script);
        };

        const resetAndReinit = () => {
            sfReadyRef.current = false;
            initialMessageSentRef.current = false;
            window.embeddedservice_bootstrap?.utilAPI?.removeAllComponents();
            const oldScript = document.getElementById("embedded-messaging-bootstrap");
            if (oldScript) oldScript.remove();
            delete window.embeddedservice_bootstrap;
            onSessionEnded?.();
            loadAndInit();
        };
        resetAndReinitRef.current = resetAndReinit;

        const initEmbeddedMessaging = () => {
            if (!window.embeddedservice_bootstrap) return;
            try {
                window.embeddedservice_bootstrap.settings.language = "en_US";
                window.embeddedservice_bootstrap.init(sfOrgId, "Internal_Chat", sfInitUrl, {
                    scrt2URL: sfScrt2Url,
                });
            } catch (err) {
                // oxlint-disable-next-line no-console
                console.error("[SupportChatPanel] Error initializing embedded messaging:", err);
            }
        };

        const handleReady = () => {
            sfReadyRef.current = true;
            if (window.embeddedservice_bootstrap?.prechatAPI) {
                window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(prechatFieldsRef.current);
            }
            if (pendingClickRef.current) {
                pendingClickRef.current = false;
                if (window.embeddedservice_bootstrap?.utilAPI) {
                    window.embeddedservice_bootstrap.utilAPI.launchChat();
                } else {
                    setTimeout(() => clickSalesforceChatButton(), 300);
                }
            }
        };

        window.addEventListener("onEmbeddedMessagingReady", handleReady);

        const handleConversationOpened = () => {
            if (initialMessageSentRef.current) return;
            const message = chatMessageRef.current;
            if (!message) return;

            const sendTextMessage = window.embeddedservice_bootstrap?.utilAPI?.sendTextMessage;
            if (!sendTextMessage) return;

            initialMessageSentRef.current = true;
            sendTextMessage(message).catch((err: unknown) => {
                // oxlint-disable-next-line no-console
                console.error("[SupportChatPanel] Failed to send automatic initial message:", err);
                initialMessageSentRef.current = false;
            });
        };

        window.addEventListener("onEmbeddedMessagingConversationOpened", handleConversationOpened);

        const handleSessionStatusUpdate = (event: Event) => {
            try {
                const payload = JSON.parse((event as CustomEvent).detail.conversationEntry.entryPayload);
                if (payload.sessionStatus === "Ended") {
                    window.embeddedservice_bootstrap?.userVerificationAPI
                        ?.clearSession(true)
                        .then(() => {
                            resetAndReinit();
                        })
                        .catch((err: unknown) => {
                            // oxlint-disable-next-line no-console
                            console.error("[SupportChatPanel] Failed to clear session", err);
                            resetAndReinit();
                        });
                }
            } catch (err) {
                // oxlint-disable-next-line no-console
                console.error("[SupportChatPanel] Failed to parse session status event", err);
            }
        };

        window.addEventListener("onEmbeddedMessagingSessionStatusUpdate", handleSessionStatusUpdate);

        // Handle race condition: event may have already fired before listener was added
        if (window.embeddedservice_bootstrap?.utilAPI) {
            handleReady();
        }

        const existingScript = document.getElementById("embedded-messaging-bootstrap") as HTMLScriptElement | null;
        if (existingScript) {
            if (window.embeddedservice_bootstrap) {
                sfReadyRef.current = true;
                initEmbeddedMessaging();
            } else {
                existingScript.addEventListener("load", initEmbeddedMessaging, { once: true });
            }
            return () => {
                existingScript.removeEventListener("load", initEmbeddedMessaging);
                window.removeEventListener("onEmbeddedMessagingReady", handleReady);
                window.removeEventListener("onEmbeddedMessagingConversationOpened", handleConversationOpened);
                window.removeEventListener("onEmbeddedMessagingSessionStatusUpdate", handleSessionStatusUpdate);
            };
        }

        loadAndInit();

        return () => {
            window.removeEventListener("onEmbeddedMessagingReady", handleReady);
            window.removeEventListener("onEmbeddedMessagingConversationOpened", handleConversationOpened);
            window.removeEventListener("onEmbeddedMessagingSessionStatusUpdate", handleSessionStatusUpdate);
        };
    }, [bootstrapUrl, sfOrgId, sfInitUrl, sfScrt2Url]);

    useEffect(() => {
        if (!open) return;
        onOpenChangeRef.current(false);

        let poll: ReturnType<typeof setInterval> | undefined;
        let pollTimeout: ReturnType<typeof setTimeout> | undefined;

        if (window.embeddedservice_bootstrap?.utilAPI) {
            window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields(prechatFieldsRef.current);
            window.embeddedservice_bootstrap.utilAPI.launchChat();
            return;
        }

        if (sfReadyRef.current) {
            window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields(prechatFieldsRef.current);
            const clicked = clickSalesforceChatButton();
            if (!clicked) {
                // Widget is gone (session ended without cleanup) — reset and re-initialize
                pendingClickRef.current = true;
                resetAndReinitRef.current?.();
            }
        } else {
            pendingClickRef.current = true;
            // Fallback poll in case onEmbeddedMessagingReady already fired or never fires
            const pollId = setInterval(() => {
                if (!pendingClickRef.current) {
                    clearInterval(pollId);
                    return;
                }
                if (window.embeddedservice_bootstrap?.utilAPI) {
                    clearInterval(pollId);
                    pendingClickRef.current = false;
                    window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields(prechatFieldsRef.current);
                    window.embeddedservice_bootstrap.utilAPI.launchChat();
                }
            }, 200);
            poll = pollId;
            pollTimeout = setTimeout(() => {
                clearInterval(pollId);
                pendingClickRef.current = false;
            }, 10000);
        }

        return () => {
            if (poll) clearInterval(poll);
            if (pollTimeout) clearTimeout(pollTimeout);
            pendingClickRef.current = false;
        };
    }, [open]);

    return null;
};

export default SupportChatPanel;
